import { useId, useState } from 'react';
import {
  assertNever,
  fillFollowUpTemplate,
  FollowUpRegister,
  followUpRegisterAllowsAmount,
  unknownFollowUpFields,
  type FollowUpSettingsDto,
  type FollowUpTemplate,
  type SaveFollowUpSettingsRequest,
} from '@signalvote/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PendingButton } from '@/components/ui/pending-button';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/** The wording being edited, one register at a time. Every slot holds real text: own or default. */
type Wording = Omit<FollowUpTemplate, 'register'>;
type Draft = { donationLink: string; wording: Record<FollowUpRegister, Wording> };

// In the order a call uses them: the link during the call, then the outcome's follow-up.
const REGISTERS = [
  FollowUpRegister.DonationLink,
  FollowUpRegister.ThankYou,
  FollowUpRegister.MissedYou,
  FollowUpRegister.ThanksForYourTime,
];

function registerLabel(register: FollowUpRegister): string {
  switch (register) {
    case FollowUpRegister.DonationLink:
      return 'Donation link';
    case FollowUpRegister.ThankYou:
      return 'Thank you';
    case FollowUpRegister.MissedYou:
      return 'Missed you';
    case FollowUpRegister.ThanksForYourTime:
      return 'Thanks for your time';
    default:
      return assertNever(register);
  }
}

function registerHint(register: FollowUpRegister): string {
  switch (register) {
    case FollowUpRegister.DonationLink:
      return "During the call, before an outcome is logged: \u201cI'm texting you the link right now.\u201d No {amount}, since nothing's committed yet.";
    case FollowUpRegister.ThankYou:
      return 'After a donor commits.';
    case FollowUpRegister.MissedYou:
      return 'After a voicemail or no answer.';
    case FollowUpRegister.ThanksForYourTime:
      return 'After a call-back request or a decline. Best without a link, so it never reads as pushy.';
    default:
      return assertNever(register);
  }
}

const defaultsFor = (settings: FollowUpSettingsDto, register: FollowUpRegister): FollowUpTemplate =>
  settings.defaults.find((t) => t.register === register)!;

function toDraft(settings: FollowUpSettingsDto): Draft {
  const wording = {} as Record<FollowUpRegister, Wording>;
  for (const register of REGISTERS) {
    const own = settings.templates.find((t) => t.register === register);
    const fallback = defaultsFor(settings, register);
    wording[register] = {
      text: own?.text ?? fallback.text,
      emailSubject: own?.emailSubject ?? fallback.emailSubject,
      emailBody: own?.emailBody ?? fallback.emailBody,
    };
  }
  return { donationLink: settings.donationLink ?? '', wording };
}

/** A slot that still says what the default says is sent empty, so it keeps following the default. */
function toSaveRequest(settings: FollowUpSettingsDto, draft: Draft): SaveFollowUpSettingsRequest {
  const own = (value: string, fallback: string) => {
    const trimmed = value.trim();
    return trimmed === '' || trimmed === fallback ? null : trimmed;
  };
  return {
    concurrencyVersion: settings.concurrencyVersion,
    donationLink: draft.donationLink.trim() || null,
    templates: REGISTERS.map((register) => {
      const fallback = defaultsFor(settings, register);
      const wording = draft.wording[register];
      return {
        register,
        text: own(wording.text, fallback.text),
        emailSubject: own(wording.emailSubject, fallback.emailSubject),
        emailBody: own(wording.emailBody, fallback.emailBody),
      };
    }),
  };
}

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

const AMOUNT_FIELD = '{amount}';

/** Whether a register's slot uses a field it can't: an unknown one, or `{amount}` where there's none. */
function hasFieldProblem(register: FollowUpRegister, value: string): boolean {
  return (
    unknownFollowUpFields(value).length > 0 ||
    (!followUpRegisterAllowsAmount(register) && value.includes(AMOUNT_FIELD))
  );
}

function FieldProblems({ register, values }: { register: FollowUpRegister; values: string[] }) {
  const unknown = [...new Set(values.flatMap(unknownFollowUpFields))];
  const amountNotAllowed =
    !followUpRegisterAllowsAmount(register) && values.some((value) => value.includes(AMOUNT_FIELD));
  if (unknown.length === 0 && !amountNotAllowed) return null;
  return (
    <>
      {unknown.length > 0 && (
        <p className="text-xs text-destructive">
          {unknown.join(', ')} {unknown.length === 1 ? "isn't a field" : "aren't fields"} a draft can use. Use{' '}
          {'{first_name}'}, {'{amount}'}, {'{link}'} or {'{candidate}'}.
        </p>
      )}
      {amountNotAllowed && (
        <p className="text-xs text-destructive">
          Only the thank-you goes out after a donor commits, so this draft can&apos;t use {AMOUNT_FIELD}.
        </p>
      )}
    </>
  );
}

function Preview({ subject, body }: { subject?: string; body: string }) {
  return (
    <div className="rounded-md bg-muted px-3 py-2 text-sm">
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Preview</p>
      {subject !== undefined && <p className="font-medium text-foreground">{subject}</p>}
      <p className="whitespace-pre-wrap text-foreground">{body}</p>
    </div>
  );
}

interface SlotProps {
  register: FollowUpRegister;
  title: string;
  hint: string;
  subject?: string;
  body: string;
  onSubject?: (value: string) => void;
  onBody: (value: string) => void;
  onReset: () => void;
  fill: (template: string) => string;
}

/** One of the eight slots: a register's text, or its email (subject + message). */
function Slot({ register, title, hint, subject, body, onSubject, onBody, onReset, fill }: SlotProps) {
  const id = useId();
  return (
    <section aria-labelledby={`${id}-title`} className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 id={`${id}-title`} className="text-sm font-semibold text-foreground">
            {title}
          </h3>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Reset to default
        </Button>
      </div>
      {subject !== undefined && onSubject && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-subject`}>Subject</Label>
          <Input id={`${id}-subject`} value={subject} onChange={(e) => onSubject(e.target.value)} />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-body`}>Message</Label>
        <textarea
          id={`${id}-body`}
          rows={4}
          className={TEXTAREA_CLASS}
          value={body}
          onChange={(e) => onBody(e.target.value)}
        />
      </div>
      <FieldProblems register={register} values={subject !== undefined ? [subject, body] : [body]} />
      <Preview subject={subject !== undefined ? fill(subject) : undefined} body={fill(body)} />
    </section>
  );
}

export interface FollowUpSettingsFormProps {
  settings: FollowUpSettingsDto;
  onSave: (request: SaveFollowUpSettingsRequest) => void;
  saving: boolean;
  /** The last save's error, shown above the Save button. */
  error: string | null;
}

/**
 * The Fundraiser's editor for the campaign's follow-up wording and donation link. Prop-driven: the
 * caller loads the settings and saves the request. Remount it (a `key` on the version) after a save so
 * it picks up the server's copy and fresh concurrency version.
 */
export function FollowUpSettingsForm({ settings, onSave, saving, error }: FollowUpSettingsFormProps) {
  const [draft, setDraft] = useState(() => toDraft(settings));
  const linkId = useId();

  const link = draft.donationLink.trim() || settings.ourDonationPageUrl;
  // Sample values: a donor called Janet who just committed $250.
  const fill = (template: string) =>
    fillFollowUpTemplate(template, {
      firstName: 'Janet',
      name: 'Janet Hollis',
      amount: 250,
      link,
      candidateName: settings.candidateName,
      campaignName: settings.campaignName,
    });

  const setWording = (register: FollowUpRegister, patch: Partial<Wording>) =>
    setDraft((d) => ({ ...d, wording: { ...d.wording, [register]: { ...d.wording[register], ...patch } } }));

  const hasFieldProblems = REGISTERS.some((register) =>
    Object.values(draft.wording[register]).some((value) => hasFieldProblem(register, value)),
  );

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (!hasFieldProblems) onSave(toSaveRequest(settings, draft));
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Follow-up donation link</CardTitle>
          <CardDescription>
            Where {'{link}'} sends donors: ActBlue, WinRed or any page. Leave it empty to use your donation
            page here once it&apos;s published.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Label htmlFor={linkId}>Follow-up donation link</Label>
          <Input
            id={linkId}
            type="url"
            inputMode="url"
            placeholder={settings.ourDonationPageUrl ?? 'https://secure.actblue.com/donate/…'}
            value={draft.donationLink}
            onChange={(e) => setDraft((d) => ({ ...d, donationLink: e.target.value }))}
          />
          {!link && (
            <p className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-600/20">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              There&apos;s no donation link, and your donation page isn&apos;t published. Follow-ups that use{' '}
              {'{link}'} will go out without a link.
            </p>
          )}
        </CardContent>
      </Card>

      {REGISTERS.map((register) => {
        const label = registerLabel(register);
        const wording = draft.wording[register];
        const fallback = defaultsFor(settings, register);
        return (
          <Card key={register}>
            <CardHeader>
              <CardTitle className="text-base">{label}</CardTitle>
              <CardDescription>{registerHint(register)}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-2">
              <Slot
                register={register}
                title={`${label} — text`}
                hint="Opens in the caller's Messages app."
                body={wording.text}
                onBody={(text) => setWording(register, { text })}
                onReset={() => setWording(register, { text: fallback.text })}
                fill={fill}
              />
              <Slot
                register={register}
                title={`${label} — email`}
                hint="Opens in the caller's own mail app."
                subject={wording.emailSubject}
                body={wording.emailBody}
                onSubject={(emailSubject) => setWording(register, { emailSubject })}
                onBody={(emailBody) => setWording(register, { emailBody })}
                onReset={() =>
                  setWording(register, { emailSubject: fallback.emailSubject, emailBody: fallback.emailBody })
                }
                fill={fill}
              />
            </CardContent>
          </Card>
        );
      })}

      <div className="flex flex-col items-end gap-2">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <PendingButton type="submit" pending={saving} disabled={hasFieldProblems}>
          Save follow-ups
        </PendingButton>
      </div>
    </form>
  );
}
